import { JsonProperty, Serializable } from 'ts-jackson'
import { Fromable, GENDER } from '../model'
import { DayjsProperty } from '@/lib/jackson'
import { Dayjs } from 'dayjs'

// Mirror of backend ReceiptStatus enum
export enum ReceiptStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  CANCELLED = 'CANCELLED',
}

@Serializable()
export class ReceiptUserInclude extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('code')
  code!: string

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string

  @JsonProperty('gender')
  gender!: GENDER
}

/** Mirror of backend `ReceiptItemType` — what a receipt line was sold for. */
export enum RECEIPT_ITEM_TYPE {
  PRODUCT = 'PRODUCT',
  PACKAGE = 'PACKAGE',
  CREDIT = 'CREDIT',
  JOINING_FEE = 'JOINING_FEE',
  /** Facility (court / room) booking. */
  BOOKING = 'BOOKING',
  /** Paid class booking — `sourceId` is the booking id as a string. */
  CLASS_BOOKING = 'CLASS_BOOKING',
}

@Serializable()
export class ReceiptItemInclude extends Fromable {
  @JsonProperty('id')
  id!: number

  /** Bundle purchases arrive as ONE line named after the bundle (QTY-2080). */
  @JsonProperty('name')
  name!: string

  /** Sub-packages the line stands for when it is a bundle; empty otherwise. */
  @JsonProperty('subItems')
  subItems?: string[]

  /** What the line stands for. Absent on receipts issued before it was stamped. */
  @JsonProperty('itemType')
  itemType?: RECEIPT_ITEM_TYPE | string | null

  /**
   * Id of the row the line was issued for, as a string — the booking id for
   * `CLASS_BOOKING` / `BOOKING`, the user-membership id for `PACKAGE`, …
   */
  @JsonProperty('sourceId')
  sourceId?: string | null
}

@Serializable()
export class Receipt extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('code')
  code!: string

  /**
   * Tax-invoice number as printed on the receipt PDF (`<prefix><YYYYMM><seq>`).
   * Issued from a per-POS/per-branch monthly counter, so it does NOT match
   * `code`. Null on receipts predating POS stamping — those fall back to `code`,
   * which is what their PDF prints too.
   */
  @JsonProperty('taxInvoiceNo')
  taxInvoiceNo?: string | null

  @JsonProperty('total')
  total!: number

  @JsonProperty('currency')
  currency!: string

  @JsonProperty('status')
  status!: ReceiptStatus

  @DayjsProperty({ path: 'issuedAt' })
  issuedAt!: Dayjs

  @JsonProperty({ path: 'member', type: ReceiptUserInclude })
  member!: ReceiptUserInclude

  @JsonProperty({ path: 'receiptItems', elementType: ReceiptItemInclude })
  receiptItems!: ReceiptItemInclude[]

  @JsonProperty('note')
  note!: string | null
}
